"use client";
import React, { useCallback, useMemo } from "react";
import ExternalButton from "@stadium/ui/src/components/button/external-button";

import { BYSTADIUM_URL } from "@stadium/helpers/app-routes";
import {
  ACCESSIBILITY_SUPPORT_KEY,
  DEFAULT_VISIBLE_LINKS,
  LINKS,
  WHITELABLED_RESTRICTED_LINKS,
} from "@stadium/ui/src/components/footer/constants";
import defaultStyles from "./main.module.scss";

interface LinkProps {
  text: string;
  link: string;
  target: string;
}

interface LinkObj {
  [key: string]: LinkProps;
}

interface FooterProps {
  bannerText: string;
  leftNavEnabledBannerText: string;
  logoText?: string;
  visibleLinks: string[];
  theme: string;
  links: LinkObj;
  displayShopsLogo?: boolean;
  storeIsWhitelabeled?: boolean;
  accessibilityEnabled?: boolean;
  footerText?: string;
  leftNavEnabled?: boolean;
  styles?: ModularStyles;
  renderCustomLinks?: () => React.ReactNode;
}

const Footer = (props: FooterProps) => {
  const {
    links,
    visibleLinks: defaultLinks = DEFAULT_VISIBLE_LINKS,
    theme = "lightMode",
    storeIsWhitelabeled = false,
    accessibilityEnabled = false,
    footerText,
    leftNavEnabled = false,
    renderCustomLinks = null,
    styles = defaultStyles,
  } = props;

  const visibleLinks =
    storeIsWhitelabeled && leftNavEnabled
      ? defaultLinks.filter((link) => !WHITELABLED_RESTRICTED_LINKS.includes(link))
      : defaultLinks;

  const filterLinks = useCallback(
    (link) => (link === ACCESSIBILITY_SUPPORT_KEY ? accessibilityEnabled : true),
    [accessibilityEnabled],
  );

  const filteredLinks = useMemo(
    () => (accessibilityEnabled ? visibleLinks : visibleLinks.filter(filterLinks)),
    [visibleLinks, filterLinks, accessibilityEnabled],
  );

  const renderLink = ({ text, link, target = "_blank", otherProps }) => {
    return (
      <li key={text} className={`${styles.footerLink}`}>
        {" "}
        <ExternalButton btnStyle="link" href={link} target={target} {...otherProps}>
          {text}
        </ExternalButton>{" "}
      </li>
    );
  };

  const renderFooterAdditionalText = () => (
    <div className={styles.footerAdditionalText}>{footerText}</div>
  );

  return (
    <footer
      className={`${styles.footer} ${leftNavEnabled ? styles.footerZIndex : ""} ${
        theme === "lightMode" ? styles.lightMode : styles.darkMode
      }`}
    >
      <div className={styles.footerContainer}>
        {footerText && renderFooterAdditionalText()}
        <div className={styles.copyRightRow} data-label="copyright-block">
          {!storeIsWhitelabeled && (
            <p className={styles.footerCopyText}>
              <ExternalButton
                btnStyle="link"
                href={BYSTADIUM_URL}
                target=""
                className={styles.copyRightText}
              >
                &copy; Powered by Stadium
              </ExternalButton>
            </p>
          )}
          {filteredLinks.length > 0 && (
            <ul className={styles.footerNav} data-label="footer-nav">
              {filteredLinks.map((linkName) => {
                const link = LINKS[linkName] || links[linkName];

                return renderLink(link);
              })}
            </ul>
          )}
          {renderCustomLinks && (
            <div className={styles.weglotDropdownContainer}>{renderCustomLinks()}</div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
